import React, { useEffect, useState, useCallback } from "react";
import { Wallet, Info, CheckCircle2, Lock, Trash2, ArrowRight, Settings2, SlidersHorizontal, Send } from "lucide-react";
import { Card, Badge, Spinner, PageHeader, Button, Modal, Input } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, BranchFilter, PeriodPicker } from "../../components/finance";

const NUM = (v) => Number(v || 0);

function ToggleRow({ label, enabled, amount, onToggle, onAmount, testid }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <label className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{label}</span>
        <input type="checkbox" checked={!!enabled} onChange={(e) => onToggle(e.target.checked)}
          data-testid={`${testid}-toggle`} className="h-4 w-4 accent-emerald-500" />
      </label>
      {enabled && (
        <input type="number" min="0" value={amount} onChange={(e) => onAmount(e.target.value)}
          data-testid={`${testid}-amount`} placeholder="Jumlah (Rp)"
          className="mt-2 w-full rounded-lg border border-line bg-app px-3 py-2 text-sm" />
      )}
    </div>
  );
}

export default function HRPayroll() {
  const toast = useToast();
  const { user, hasPermission } = useAuth();
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const canManage = hasPermission("manage_payroll");
  const [branchId, setBranchId] = useState("");
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [sendingAdv, setSendingAdv] = useState(false);
  const [cfg, setCfg] = useState(null);      // {emp_id, employee_name, ...config}
  const [adj, setAdj] = useState(null);      // {emp_id, employee_name, ...adjustments}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pv, rn] = await Promise.all([
        api.get("/hr/payroll-preview", { params: { period, branch_id: branchId || undefined } }),
        api.get("/hr/payroll/runs", { params: { period } }),
      ]);
      setData(pv.data); setRuns(rn.data.runs);
    } catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [period, branchId, toast]);
  useEffect(() => { load(); }, [load]);

  const finalize = async () => {
    if (!window.confirm(`Finalisasi Gaji Final ${period}${branchId ? " (cabang terpilih)" : " (semua cabang)"}?\n\nIni mengunci nilai & membuat entri Finance + jurnal Accounting otomatis.`)) return;
    setFinalizing(true);
    try {
      const { data: res } = await api.post("/hr/payroll/finalize", null, { params: { period, branch_id: branchId || undefined } });
      toast.success(`Payroll difinalisasi. Advance ${rp(res.run.total_advance)} · Final ${rp(res.run.total_final)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setFinalizing(false); }
  };

  const sendAdvance = async () => {
    if (!window.confirm(`Kirim notifikasi Slip Advance ${period} ke karyawan?`)) return;
    setSendingAdv(true);
    try {
      const { data: res } = await api.post("/hr/payroll/advance", null, { params: { period, branch_id: branchId || undefined } });
      toast.success(`Slip advance dikirim ke ${res.notified} karyawan`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSendingAdv(false); }
  };

  const deleteRun = async (id) => {
    if (!window.confirm("Batalkan finalisasi payroll ini? Entri Finance & jurnal Accounting terkait akan dihapus.")) return;
    try { await api.delete(`/hr/payroll/runs/${id}`); toast.success("Finalisasi dibatalkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openConfig = async (r) => {
    try { const { data: c } = await api.get(`/hr/payroll-config/${r.employee_id}`); setCfg({ ...c, employee_name: r.employee_name }); }
    catch { toast.error("Gagal memuat konfigurasi"); }
  };
  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/hr/payroll-config/${cfg.employee_id}`, {
        advance_amount: NUM(cfg.advance_amount), advance_day: NUM(cfg.advance_day) || 15,
        final_day: cfg.final_day === "" || cfg.final_day == null ? null : NUM(cfg.final_day),
        bpjs_kes_enabled: !!cfg.bpjs_kes_enabled, bpjs_kes_amount: NUM(cfg.bpjs_kes_amount),
        bpjs_tk_enabled: !!cfg.bpjs_tk_enabled, bpjs_tk_amount: NUM(cfg.bpjs_tk_amount),
        pph21_enabled: !!cfg.pph21_enabled, pph21_amount: NUM(cfg.pph21_amount),
      });
      toast.success("Konfigurasi disimpan"); setCfg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  const openAdj = async (r) => {
    try { const { data: a } = await api.get(`/hr/payroll-adjustments/${r.employee_id}`, { params: { period } }); setAdj({ ...a, employee_name: r.employee_name }); }
    catch { toast.error("Gagal memuat penyesuaian"); }
  };
  const saveAdj = async () => {
    setSaving(true);
    try {
      await api.put(`/hr/payroll-adjustments/${adj.employee_id}`, {
        bonus: NUM(adj.bonus), overtime: NUM(adj.overtime), incentive: NUM(adj.incentive),
        loan: NUM(adj.loan), deduction: NUM(adj.deduction), note: adj.note || null,
      }, { params: { period } });
      toast.success("Penyesuaian disimpan"); setAdj(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="hr-payroll-page">
      <PageHeader title="Payroll — Fixed Advance" subtitle="Advance (tgl 15) + Gaji Final (akhir bulan). Finalisasi mengalir ke Finance & Accounting." icon={Wallet}>
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-payroll-branch-filter" />}
        <PeriodPicker value={period} onChange={setPeriod} />
        {canManage && <Button variant="secondary" onClick={sendAdvance} loading={sendingAdv} data-testid="hr-payroll-advance-btn"><Send size={15} /> Kirim Slip Advance</Button>}
        {canManage && <Button onClick={finalize} loading={finalizing} data-testid="hr-payroll-finalize-btn"><CheckCircle2 size={15} /> Finalisasi Gaji Final</Button>}
      </PageHeader>

      {loading || !data ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-4">
          {runs.length > 0 && (
            <Card data-testid="hr-payroll-runs">
              <h3 className="mb-2 font-heading font-bold text-ink">Payroll Difinalisasi — {period}</h3>
              <div className="space-y-1.5">
                {runs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-xl border border-line px-3 py-2" data-testid={`hr-payroll-run-${r.id}`}>
                    <span className="flex items-center gap-2 text-sm"><Lock size={13} className="text-emerald-600" /><span className="font-semibold text-ink">{r.branch_name}</span><Badge tone="mute">{r.employee_count} kary.</Badge></span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm tabular-nums text-ink">Net {rp(r.total_net)}</span>
                      {canManage && <button onClick={() => deleteRun(r.id)} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" data-testid={`hr-payroll-delete-${r.id}`}><Trash2 size={14} /></button>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 flex items-center gap-1 text-xs text-emerald-700"><ArrowRight size={12} /> Tercatat di Finance (Beban Gaji), Buku Besar (5100/2200), dan terbaca AI CFO.</p>
            </Card>
          )}

          <div className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-xs text-blue-800" data-testid="hr-payroll-note">
            <Info size={14} /> Hanya <b>Gaji Bulanan</b> yang wajib. Bonus, Lembur, Insentif, Pinjaman, Potongan, BPJS & PPh21 bersifat opsional per karyawan — yang kosong/nonaktif diabaikan.
          </div>

          <Card className="overflow-x-auto" data-testid="hr-payroll-table">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold text-ink">Estimasi Payroll {period}</h3>
              <Badge tone="info">Total Net {rp(data.total_estimated)}</Badge>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <tr>
                  <th className="py-2 pr-3">Karyawan</th>
                  <th className="py-2 pr-3">Posisi</th>
                  <th className="py-2 pr-3 text-right">Gaji Bulanan</th>
                  <th className="py-2 pr-3 text-right">Advance</th>
                  <th className="py-2 pr-3 text-right">Penyesuaian</th>
                  <th className="py-2 pr-3 text-right">Potongan</th>
                  <th className="py-2 pr-3 text-right">Gaji Final</th>
                  <th className="py-2 pr-3 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.rows.map((r) => {
                  const adds = NUM(r.earnings?.bonus) + NUM(r.earnings?.overtime) + NUM(r.earnings?.incentive);
                  return (
                    <tr key={r.employee_id} data-testid={`hr-payroll-row-${r.employee_id}`}>
                      <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.position}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-ink">{rp(r.monthly_salary)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-ink-soft">{rp(r.advance_amount)}<span className="ml-1 text-[10px] text-ink-mute">tgl {r.advance_day}</span></td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700">{adds > 0 ? `+${rp(adds)}` : "—"}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-rose-600">{NUM(r.total_deductions) > 0 ? `−${rp(r.total_deductions)}` : "—"}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold tabular-nums text-primary" data-testid={`hr-payroll-final-${r.employee_id}`}>{rp(r.final_net)}</td>
                      <td className="py-2.5 pr-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openConfig(r)} title="Konfigurasi Gaji" className="rounded-lg p-1.5 text-ink-soft hover:bg-app" data-testid={`hr-payroll-config-${r.employee_id}`}><Settings2 size={15} /></button>
                          {canManage && <button onClick={() => openAdj(r)} title="Penyesuaian Bulan Ini" className="rounded-lg p-1.5 text-ink-soft hover:bg-app" data-testid={`hr-payroll-adjust-${r.employee_id}`}><SlidersHorizontal size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Per-employee payroll config */}
      <Modal open={!!cfg} onClose={() => setCfg(null)} title={`Konfigurasi Gaji — ${cfg?.employee_name || ""}`} size="md"
        footer={canManage && <Button onClick={saveConfig} loading={saving} data-testid="hr-payroll-config-save">Simpan</Button>}>
        {cfg && (
          <div className="space-y-3" data-testid="hr-payroll-config-modal">
            <div className="rounded-xl bg-app px-3 py-2 text-xs text-ink-soft">Gaji Bulanan: <b className="text-ink">{rp(cfg.monthly_salary)}</b> <span className="text-ink-mute">(ubah di menu Karyawan)</span></div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Jumlah Advance (Rp)" type="number" min="0" value={cfg.advance_amount ?? 0} onChange={(e) => setCfg({ ...cfg, advance_amount: e.target.value })} data-testid="cfg-advance-amount" disabled={!canManage} />
              <Input label="Tanggal Advance" type="number" min="1" max="28" value={cfg.advance_day ?? 15} onChange={(e) => setCfg({ ...cfg, advance_day: e.target.value })} data-testid="cfg-advance-day" disabled={!canManage} />
            </div>
            <Input label="Tanggal Final (kosong = akhir bulan)" type="number" min="1" max="31" value={cfg.final_day ?? ""} onChange={(e) => setCfg({ ...cfg, final_day: e.target.value })} data-testid="cfg-final-day" disabled={!canManage} />
            <p className="pt-1 text-xs font-semibold text-ink-soft">Potongan Opsional (per karyawan)</p>
            <ToggleRow label="BPJS Kesehatan" enabled={cfg.bpjs_kes_enabled} amount={cfg.bpjs_kes_amount ?? 0}
              onToggle={(v) => setCfg({ ...cfg, bpjs_kes_enabled: v })} onAmount={(v) => setCfg({ ...cfg, bpjs_kes_amount: v })} testid="cfg-bpjs-kes" />
            <ToggleRow label="BPJS Ketenagakerjaan" enabled={cfg.bpjs_tk_enabled} amount={cfg.bpjs_tk_amount ?? 0}
              onToggle={(v) => setCfg({ ...cfg, bpjs_tk_enabled: v })} onAmount={(v) => setCfg({ ...cfg, bpjs_tk_amount: v })} testid="cfg-bpjs-tk" />
            <ToggleRow label="PPh 21 (Pajak Penghasilan)" enabled={cfg.pph21_enabled} amount={cfg.pph21_amount ?? 0}
              onToggle={(v) => setCfg({ ...cfg, pph21_enabled: v })} onAmount={(v) => setCfg({ ...cfg, pph21_amount: v })} testid="cfg-pph21" />
          </div>
        )}
      </Modal>

      {/* Per-period adjustments */}
      <Modal open={!!adj} onClose={() => setAdj(null)} title={`Penyesuaian ${period} — ${adj?.employee_name || ""}`} size="md"
        footer={<Button onClick={saveAdj} loading={saving} data-testid="hr-payroll-adjust-save">Simpan</Button>}>
        {adj && (
          <div className="space-y-3" data-testid="hr-payroll-adjust-modal">
            <p className="text-xs text-ink-mute">Semua opsional. Kosongkan (0) jika tidak ada.</p>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Bonus (Rp)" type="number" min="0" value={adj.bonus ?? 0} onChange={(e) => setAdj({ ...adj, bonus: e.target.value })} data-testid="adj-bonus" />
              <Input label="Lembur (Rp)" type="number" min="0" value={adj.overtime ?? 0} onChange={(e) => setAdj({ ...adj, overtime: e.target.value })} data-testid="adj-overtime" />
              <Input label="Insentif (Rp)" type="number" min="0" value={adj.incentive ?? 0} onChange={(e) => setAdj({ ...adj, incentive: e.target.value })} data-testid="adj-incentive" />
              <Input label="Pinjaman/Kasbon (Rp)" type="number" min="0" value={adj.loan ?? 0} onChange={(e) => setAdj({ ...adj, loan: e.target.value })} data-testid="adj-loan" />
              <Input label="Potongan Lain (Rp)" type="number" min="0" value={adj.deduction ?? 0} onChange={(e) => setAdj({ ...adj, deduction: e.target.value })} data-testid="adj-deduction" />
            </div>
            <Input label="Catatan (opsional)" value={adj.note ?? ""} onChange={(e) => setAdj({ ...adj, note: e.target.value })} data-testid="adj-note" />
          </div>
        )}
      </Modal>
    </div>
  );
}
